import { useAppQuery } from '@/hooks/useAppQuery';
import { api } from '@/lib/api-client';
import {
  type LanguageOption,
  type GetTranslationLanguagesResponse,
} from '@/models/translation';

import { translationsQueryKey } from './queryKeys';

export const getTranslationLanguages = async (): Promise<LanguageOption[]> => {
  const response =
    await api.get<GetTranslationLanguagesResponse>(`/v1/languages`);
  // The default is server-owned (per-language `is_default`). An empty scope has
  // no languages and no default — callers handle that empty case.
  return response.data.languages.map((language) => ({
    locale: language.language,
    default: language.is_default,
    selected: language.is_default,
    translatedPercent: language.translated_percent,
  }));
};

/**
 * Loaded via a route loader (`ensureQueryData`), so the key must carry the
 * game/sandbox scope explicitly — accessible locales differ per sandbox and
 * this mirrors the key shape `useAppQuery` would produce. The fetch itself
 * needs no scope args: the axios client injects `game_code`/`sandbox_code`.
 */
export const getTranslationLanguagesQuery = (
  gameCode: string,
  sandboxCode: string,
) => ({
  queryKey: [gameCode, sandboxCode, ...translationsQueryKey.languages()],
  queryFn: getTranslationLanguages,
});

/**
 * Sandbox-scoped languages available to any component. The query cache is the
 * store: the key carries game/sandbox (via `useAppQuery`), so switching sandbox
 * refetches and every caller shares one cached list.
 */
export const useGetTranslationLanguages = () =>
  useAppQuery({
    queryKey: translationsQueryKey.languages(),
    queryFn: getTranslationLanguages,
  });
